import type { ChangelogEntry, CreateChangelogEntryPayload, UpdateChangelogEntryPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type ChangelogEntriesResponse = { changelog_entries: ChangelogEntry[] };
type ChangelogEntryResponse = { changelog_entry: ChangelogEntry; message: string };
type MessageResponse = { message: string };

const changelogListTags = (result: ChangelogEntry[] | undefined, listId: 'LIST' | 'PUBLIC_LIST') =>
  result
    ? [...result.map(({ id }) => ({ type: 'Changelog' as const, id })), { type: 'Changelog' as const, id: listId }]
    : [{ type: 'Changelog' as const, id: listId }];

/**
 * Pilot domain validating the shared RTK Query mechanism end to end. Mirrors
 * `apps/server/src/modules/changelog` (`changelogPublicController` + `changelogAdminController`)
 * exactly, matching the contract previously duplicated across `apps/web`'s
 * `admin-user/stores/usecases/*-changelog-entry.ts`.
 */
export const changelogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicChangelogEntries: builder.query<ChangelogEntry[], void>({
      query: () => ({ path: ['changelog'], method: 'get' }),
      transformResponse: (response: ChangelogEntriesResponse) => response.changelog_entries,
      providesTags: (result) => changelogListTags(result, 'PUBLIC_LIST'),
    }),

    getChangelogEntries: builder.query<ChangelogEntry[], void>({
      query: () => ({ path: ['admin', 'changelog'], method: 'get' }),
      transformResponse: (response: ChangelogEntriesResponse) => response.changelog_entries,
      providesTags: (result) => changelogListTags(result, 'LIST'),
    }),

    createChangelogEntry: builder.mutation<ChangelogEntry, CreateChangelogEntryPayload>({
      query: (body) => ({ path: ['admin', 'changelog'], method: 'post', body }),
      transformResponse: (response: ChangelogEntryResponse) => response.changelog_entry,
      invalidatesTags: [
        { type: 'Changelog', id: 'LIST' },
        { type: 'Changelog', id: 'PUBLIC_LIST' },
      ],
    }),

    updateChangelogEntry: builder.mutation<ChangelogEntry, { id: string; payload: UpdateChangelogEntryPayload }>({
      query: ({ id, payload }) => ({
        path: ['admin', 'changelog', { param: 'id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: ChangelogEntryResponse) => response.changelog_entry,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Changelog', id },
        { type: 'Changelog', id: 'LIST' },
        { type: 'Changelog', id: 'PUBLIC_LIST' },
      ],
    }),

    deleteChangelogEntry: builder.mutation<MessageResponse, string>({
      query: (id) => ({ path: ['admin', 'changelog', { param: 'id', value: id }], method: 'delete' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Changelog', id },
        { type: 'Changelog', id: 'LIST' },
        { type: 'Changelog', id: 'PUBLIC_LIST' },
      ],
    }),
  }),
});

export const {
  useGetPublicChangelogEntriesQuery,
  useGetChangelogEntriesQuery,
  useCreateChangelogEntryMutation,
  useUpdateChangelogEntryMutation,
  useDeleteChangelogEntryMutation,
} = changelogApi;
